import GroupsIcon from "@mui/icons-material/Groups";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ButtonBase from "@mui/material/ButtonBase";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import React, { useRef, useState } from "react";

import { TestIds } from "@storyboard/web-test-ids/testIds";
import { zinc } from "@/ui/tokens";

/**
 * The team (channel) picker beside the view name, the same choice the driver list offers: which of a driver's alias
 * channels the rows read from and open. Hidden unless the workspace keys more than one team. The choice is the
 * member's `dispatcherFunction`, shared with the driver list, so both pages agree.
 */
export default React.memo(function DispatchTeamPicker({
  team,
  teams,
  onChange,
}: {
  team: string;
  teams: readonly string[];
  onChange: (team: string) => void;
}) {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  if (teams.length < 2) return null;
  return (
    <>
      <ButtonBase
        ref={anchorRef}
        data-sb={TestIds.dispatch.teamDropdown}
        aria-label="Team"
        aria-haspopup="menu"
        onClick={() => setOpen(true)}
        sx={{
          height: 28,
          px: 1,
          gap: 0.5,
          borderRadius: 1,
          color: zinc[200],
          fontSize: "0.875rem",
          whiteSpace: "nowrap",
          "&:hover": { bgcolor: zinc[800], color: "white" },
        }}
      >
        <GroupsIcon sx={{ fontSize: 18, color: zinc[400] }} />
        {team}
        <KeyboardArrowDownIcon sx={{ fontSize: 16, color: zinc[400] }} />
      </ButtonBase>
      <Menu
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        slotProps={{ paper: { sx: { bgcolor: zinc[800], color: "white", minWidth: 180 } }, list: { dense: true } }}
      >
        {teams.map((candidate) => (
          <MenuItem
            key={candidate}
            data-sb={TestIds.dispatch.teamOption}
            selected={candidate === team}
            onClick={() => {
              onChange(candidate);
              setOpen(false);
            }}
            sx={{ fontSize: "0.8125rem" }}
          >
            {candidate}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
});
